import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiLoader, 
  FiEye,
  FiSave,
  FiX,
  FiSearch,
  FiMapPin,
  FiPhone,
  FiMail,
  FiChevronLeft,
  FiChevronRight,
  FiHome
} from "react-icons/fi";

const CompanyContacts = () => {
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  
  // Form states
  const [newContact, setNewContact] = useState({ mobile: "", email: "", address: "" });
  const [editingContact, setEditingContact] = useState({ id: null, mobile: "", email: "", address: "" });
  
  // Loading states
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const API_URL = "https://api.dreamtoday.in/api/admin";

  // Fetch all company contacts
  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/allcompanycontacts`, {
        withCredentials: true
      });
      
      console.log("Company contacts response:", response.data);
      
      if (response.data.success) {
        setContacts(response.data.contacts || []);
        setFilteredContacts(response.data.contacts || []);
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
      toast.error("Failed to fetch contacts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  // Search and filter
  useEffect(() => {
    if (searchTerm) {
      const filtered = contacts.filter(contact => 
        contact.mobile?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact._id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredContacts(filtered);
    } else {
      setFilteredContacts(contacts);
    }
    setCurrentPage(1);
  }, [searchTerm, contacts]);

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredContacts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredContacts.length / itemsPerPage);

  // Handle create contact
  const handleCreateContact = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      if (!newContact.mobile || !newContact.email || !newContact.address) {
        toast.error("All fields are required");
        setCreating(false);
        return;
      }

      const response = await axios.post(
        `${API_URL}/addcompanycontact`,
        newContact,
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("Company contact created successfully!");
        setShowCreateModal(false);
        setNewContact({ mobile: "", email: "", address: "" });
        fetchContacts();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create contact");
    } finally {
      setCreating(false);
    }
  };

  // Handle update contact
  const handleUpdateContact = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      if (!editingContact.mobile || !editingContact.email || !editingContact.address) {
        toast.error("All fields are required");
        setUpdating(false);
        return;
      }

      const response = await axios.put(
        `${API_URL}/updatecompanycontact/${editingContact.id}`,
        {
          mobile: editingContact.mobile,
          email: editingContact.email,
          address: editingContact.address
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("Contact updated successfully!");
        setShowEditModal(false);
        setEditingContact({ id: null, mobile: "", email: "", address: "" });
        fetchContacts();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update contact");
    } finally {
      setUpdating(false);
    }
  };

  // Handle delete contact
  const handleDeleteContact = async () => {
    if (!selectedContact) return;
    
    setDeleting(true);
    try {
      const response = await axios.delete(
        `${API_URL}/deletecompanycontact/${selectedContact._id}`,
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("Contact deleted successfully!");
        setShowDeleteModal(false);
        setSelectedContact(null);
        fetchContacts();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete contact");
    } finally {
      setDeleting(false);
    }
  };

  // Format date
  const formatDate = (date) => new Date(date).toLocaleDateString();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <FiLoader className="animate-spin text-4xl text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <FiHome className="text-blue-500" />
              Company Contacts
            </h1>
            <p className="text-gray-600 mt-1">Total {filteredContacts.length} contacts found</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <FiPlus /> Add Contact
          </button>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="relative">
            <FiSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search by mobile, email, address or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Table */}
        {currentItems.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <FiHome className="text-6xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Contacts Found</h3>
            <p className="text-gray-500 mb-6">Get started by adding your first company contact</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Add Contact
            </button>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">S.No</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Mobile</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Address</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Created</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {currentItems.map((contact, idx) => (
                      <tr key={contact._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {indexOfFirstItem + idx + 1}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <FiPhone className="text-green-600" />
                            <span className="text-sm">{contact.mobile}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <FiMail className="text-blue-600" />
                            <span className="text-sm">{contact.email}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 max-w-xs">
                            <FiMapPin className="text-red-600 flex-shrink-0" />
                            <span className="text-sm truncate">{contact.address}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {formatDate(contact.createdAt)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setSelectedContact(contact);
                                setShowViewModal(true);
                              }}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                              title="View"
                            >
                              <FiEye />
                            </button>
                            <button
                              onClick={() => {
                                setEditingContact({
                                  id: contact._id,
                                  mobile: contact.mobile,
                                  email: contact.email,
                                  address: contact.address
                                });
                                setShowEditModal(true);
                              }}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                              title="Edit"
                            >
                              <FiEdit2 />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedContact(contact);
                                setShowDeleteModal(true);
                              }}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredContacts.length)} of {filteredContacts.length}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    <FiChevronLeft />
                  </button>
                  <span className="px-4 py-2 bg-blue-600 text-white rounded-lg">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    <FiChevronRight />
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">Add Company Contact</h2>
                  <button onClick={() => setShowCreateModal(false)} className="p-1 hover:bg-gray-100 rounded">
                    <FiX />
                  </button>
                </div>

                <form onSubmit={handleCreateContact} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1 flex items-center gap-2">
                      <FiPhone className="text-green-600" /> Mobile Number *
                    </label>
                    <input
                      type="tel"
                      value={newContact.mobile}
                      onChange={(e) => setNewContact({...newContact, mobile: e.target.value})}
                      className="w-full p-2 border rounded"
                      placeholder="+91 98765 43210"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1 flex items-center gap-2">
                      <FiMail className="text-blue-600" /> Email Address *
                    </label>
                    <input
                      type="email"
                      value={newContact.email}
                      onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                      className="w-full p-2 border rounded"
                      placeholder="contact@company.com"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1 flex items-center gap-2">
                      <FiMapPin className="text-red-600" /> Address *
                    </label>
                    <textarea
                      value={newContact.address}
                      onChange={(e) => setNewContact({...newContact, address: e.target.value})}
                      rows="3"
                      className="w-full p-2 border rounded"
                      placeholder="123 Business Street, City, State - 400001"
                      required
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={creating}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {creating ? "Creating..." : "Create"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">Edit Contact</h2>
                  <button onClick={() => setShowEditModal(false)} className="p-1 hover:bg-gray-100 rounded">
                    <FiX />
                  </button>
                </div>

                <form onSubmit={handleUpdateContact} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1 flex items-center gap-2">
                      <FiPhone className="text-green-600" /> Mobile Number *
                    </label>
                    <input
                      type="tel"
                      value={editingContact.mobile}
                      onChange={(e) => setEditingContact({...editingContact, mobile: e.target.value})}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1 flex items-center gap-2">
                      <FiMail className="text-blue-600" /> Email Address *
                    </label>
                    <input
                      type="email"
                      value={editingContact.email}
                      onChange={(e) => setEditingContact({...editingContact, email: e.target.value})}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1 flex items-center gap-2">
                      <FiMapPin className="text-red-600" /> Address *
                    </label>
                    <textarea
                      value={editingContact.address}
                      onChange={(e) => setEditingContact({...editingContact, address: e.target.value})}
                      rows="3"
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={updating}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      {updating ? "Updating..." : "Update"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* View Modal */}
        {showViewModal && selectedContact && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">Contact Details</h2>
                  <button onClick={() => setShowViewModal(false)} className="p-1 hover:bg-gray-100 rounded">
                    <FiX />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1">Contact ID</p>
                    <p className="text-sm font-mono break-all">{selectedContact._id}</p>
                  </div>

                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <FiPhone className="text-green-600" /> Mobile
                    </p>
                    <p className="text-sm">{selectedContact.mobile}</p>
                  </div>

                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <FiMail className="text-blue-600" /> Email
                    </p>
                    <p className="text-sm break-all">{selectedContact.email}</p>
                  </div>

                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <FiMapPin className="text-red-600" /> Address
                    </p>
                    <p className="text-sm whitespace-pre-wrap break-words">{selectedContact.address}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-xs text-gray-500">Created</p>
                      <p className="text-sm">{formatDate(selectedContact.createdAt)}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-xs text-gray-500">Updated</p>
                      <p className="text-sm">{formatDate(selectedContact.updatedAt)}</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      setEditingContact({
                        id: selectedContact._id,
                        mobile: selectedContact.mobile,
                        email: selectedContact.email,
                        address: selectedContact.address
                      });
                      setShowEditModal(true);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Modal */}
        {showDeleteModal && selectedContact && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="text-center">
                <FiTrash2 className="text-5xl text-red-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Delete Contact</h3>
                <p className="text-gray-600 mb-6">
                  Are you sure you want to delete this company contact?
                </p>
                
                <div className="flex justify-center gap-3">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteContact}
                    disabled={deleting}
                    className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {deleting ? <FiLoader className="animate-spin" /> : <FiTrash2 />}
                    {deleting ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyContacts;